import React, { createContext, useContext } from 'react';
import { useQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import {
  ModuleHeader,
  useEditContext,
  usePageContext,
  Carousel,
} from '@xingternal/pages-common';
import { ErrorContext } from '@xingternal/pages-page-error';
import Error from '@xingternal/pages-warning';
import { withServerData } from '@xingternal/hops';
import { getTrackingUrl } from '@xingternal/pages-tracking';

import EmployeesCarousel from './EmployeesCarousel';
import { EmployeesCardSkeleton } from './EmployeesCardVertical';
import EMPLOYEES_QUERY from '../graphql/EmployeesQuery.graphql';
import {
  NUMBER_OF_EMPLOYEES_TO_SHOW_LOGGED_IN,
  NUMBER_OF_EMPLOYEES_TO_SHOW_LOGGED_OUT,
  EMPLOYEES_OFFSET_LOGGED_OUT,
  EMPLOYEES_OFFSET_LOGGED_IN,
} from './config';
import styles from './EmployeesContainer.css';

import { useNetworkRequestMutation } from './../hooks/useNetworkRequestMutation';
import { useNetworkRequestLoggedOutAction } from './../hooks/useNetworkRequestLoggedOutAction';

import {
  QUERY_CONSUMER_CAROUSEL,
  EMPLOYEES_SORTING,
  updateCurrentCache,
  getCurrentCache,
} from '../utils';
import { EmployeeNode } from '../types';

const DEFAULT_ERROR_POLICY = 'all';
const MINIMUM_EMPLOYEES_FOR_DETAIL_LINK = 4;

export const trackingKey = 'viewAllEmployees';

type EmployeesProps = {
  companyId: string;
  pageSlug: string;
  serverData: { authenticated: boolean };
};

type EmployeesContainerProps = EmployeesProps;

type RenderErrorProps = {
  refetch: () => void;
  isEditing: boolean;
};

export const EmployeesContext = createContext<{ companyId?: string }>({});

const getCacheParams = (cacheParams: any, hasPhoto?: boolean) => {
  if (hasPhoto) {
    return {
      ...cacheParams,
      variables: {
        ...cacheParams.variables,
        includeTotalQuery: true,
        consumer: QUERY_CONSUMER_CAROUSEL,
        query: {
          ...cacheParams.variables.query,
          filters: {
            ...cacheParams.variables.query.filters,
            hasPhoto: true,
          },
        },
      },
    };
  } else if (hasPhoto === false) {
    return {
      ...cacheParams,
      variables: {
        ...cacheParams.variables,
        query: {
          ...cacheParams.variables.query,
          filters: {
            ...cacheParams.variables.query.filters,
            hasPhoto: false,
          },
        },
      },
    };
  }

  return cacheParams;
};

const RenderError = ({ refetch, isEditing }: RenderErrorProps) => (
  <div data-testid="errorContainer">
    <ModuleHeader
      headlineCopyKey="EP_EMPLOYEES_HEADLINE"
      externalEditInfo={
        isEditing
          ? {
              copyKey: 'EP_EMPLOYEES_EDIT_HINT',
              cyTestId: 'EDIT_HINT_EMPLOYEES',
            }
          : null
      }
    />
    <Error
      headerText="EP_ERROR_HEADER"
      bodyText="EP_ERROR_BODY"
      buttonText="EP_ERROR_RELOAD_CTA"
      onClick={() => refetch()}
    />
  </div>
);

const RenderLoading = () => (
  <>
    <ModuleHeader
      headlineCopyKey="EP_EMPLOYEES_HEADLINE"
      showMore={{
        isLoading: true,
      }}
    />
    <Carousel
      skin={{ carousel: styles.carousel, carouselMain: styles.carouselMain }}
    >
      <EmployeesCardSkeleton />
      <EmployeesCardSkeleton />
      <EmployeesCardSkeleton />
      <EmployeesCardSkeleton />
      <EmployeesCardSkeleton />
    </Carousel>
  </>
);

const RenderEmptyState = () => (
  <>
    <ModuleHeader headlineCopyKey="EP_EMPLOYEES_HEADLINE" />
    <div className={styles.emptyState}>
      <FormattedMessage id="EP_EMPLOYEES_EMPTY_STATE" />
    </div>
  </>
);

const Employees = ({
  pageSlug,
  companyId,
  serverData: { authenticated = false },
}: EmployeesProps) => {
  const { showError } = useContext(ErrorContext);
  const { pageContext } = usePageContext();
  const { isEditing } = useEditContext();
  const numberOfEmployeesToShow = authenticated
    ? NUMBER_OF_EMPLOYEES_TO_SHOW_LOGGED_IN * EMPLOYEES_OFFSET_LOGGED_IN
    : NUMBER_OF_EMPLOYEES_TO_SHOW_LOGGED_OUT * EMPLOYEES_OFFSET_LOGGED_OUT;

  const {
    data: employeesWithProfilePicture,
    loading: loadingEmployeesWithProfilePicture,
    refetch: refetchEmployeesWithProfilePicture,
  } = useQuery(EMPLOYEES_QUERY, {
    variables: {
      id: companyId,
      first: numberOfEmployeesToShow,
      includeTotalQuery: true,
      consumer: QUERY_CONSUMER_CAROUSEL,
      query: {
        consumer: QUERY_CONSUMER_CAROUSEL,
        sort: EMPLOYEES_SORTING,
        filters: {
          hasPhoto: true,
        },
      },
    },
    errorPolicy: DEFAULT_ERROR_POLICY,
  });

  const {
    data: employeesWithoutProfilePicture,
    loading: loadingEmployeesWithoutProfilePicture,
    refetch: refetchEmployeesWithoutProfilePicture,
  } = useQuery(EMPLOYEES_QUERY, {
    skip:
      loadingEmployeesWithProfilePicture ||
      employeesWithProfilePicture?.company?.employees?.total >=
        numberOfEmployeesToShow,
    variables: {
      id: companyId,
      first:
        numberOfEmployeesToShow -
        (employeesWithProfilePicture?.company?.employees?.edges.length || 0),
      query: {
        consumer: QUERY_CONSUMER_CAROUSEL,
        sort: EMPLOYEES_SORTING,
        filters: {
          hasPhoto: false,
        },
      },
    },
    errorPolicy: DEFAULT_ERROR_POLICY,
  });

  const updateEmployeesCache = (
    cache: any,
    { data }: any,
    id: string,
    hasPhoto?: boolean
  ) => {
    if (data?.networkContactRequestsSend?.error?.message) {
      return showError({
        message: 'EP_JOBS_FEEDBACK_ERROR',
        error: data.networkContactRequestsSend.error.message,
      });
    }

    const currentCacheParams = {
      cache,
      companyId,
      query: EMPLOYEES_QUERY,
      consumer: QUERY_CONSUMER_CAROUSEL,
      variables: {
        id: companyId,
        first:
          NUMBER_OF_EMPLOYEES_TO_SHOW_LOGGED_IN * EMPLOYEES_OFFSET_LOGGED_IN,
        query: {
          consumer: QUERY_CONSUMER_CAROUSEL,
          sort: EMPLOYEES_SORTING,
        },
      },
    };

    const { viewer, edges, total, pageInfo } = getCurrentCache(
      getCacheParams(currentCacheParams, hasPhoto)
    );

    const updateCurrentCacheParams = {
      viewer,
      cache,
      id,
      edges,
      total,
      pageInfo,
      query: EMPLOYEES_QUERY,
      variables: {
        id: companyId,
        first: NUMBER_OF_EMPLOYEES_TO_SHOW_LOGGED_IN,
        query: {
          consumer: QUERY_CONSUMER_CAROUSEL,
        },
      },
    };

    updateCurrentCache(getCacheParams(updateCurrentCacheParams, hasPhoto));
  };

  const [executeNetworkRequest] = useNetworkRequestMutation({
    showError,
    update: updateEmployeesCache,
  });

  const loading =
    loadingEmployeesWithProfilePicture || loadingEmployeesWithoutProfilePicture;

  const error =
    !employeesWithProfilePicture &&
    !loadingEmployeesWithProfilePicture &&
    !employeesWithoutProfilePicture &&
    !loadingEmployeesWithoutProfilePicture;

  const totalEmployees =
    employeesWithProfilePicture?.company?.totalEmployees?.total;

  const currentUserId =
    employeesWithoutProfilePicture?.viewer?.xingId?.id ||
    employeesWithProfilePicture?.viewer?.xingId?.id;

  const addHasPhoto = (employee: EmployeeNode, hasPhoto: boolean) => ({
    ...employee,
    node: {
      hasPhoto,
      ...employee.node,
    },
  });

  const employeesListWithProfilePicture =
    employeesWithProfilePicture?.company?.employees?.edges.map(
      (employee: EmployeeNode) => addHasPhoto(employee, true)
    ) || [];
  const employeesListWithoutProfilePicture =
    employeesWithoutProfilePicture?.company?.employees?.edges.map(
      (employee: EmployeeNode) => addHasPhoto(employee, false)
    ) || [];

  const employeesList = [
    ...employeesListWithProfilePicture,
    ...employeesListWithoutProfilePicture,
  ];

  useNetworkRequestLoggedOutAction({
    authenticated,
    employees: employeesList,
    executeNetworkRequest,
    loading,
  });

  if (loading) {
    return <RenderLoading />;
  }

  if (error) {
    return (
      <RenderError
        refetch={() => {
          refetchEmployeesWithoutProfilePicture();
          refetchEmployeesWithProfilePicture();
        }}
        isEditing={isEditing}
      />
    );
  }

  if (totalEmployees === 0) {
    return <RenderEmptyState />;
  }

  if (totalEmployees === undefined || employeesList === undefined) {
    return (
      <RenderError
        refetch={() => {
          refetchEmployeesWithProfilePicture();
          refetchEmployeesWithoutProfilePicture();
        }}
        isEditing={isEditing}
      />
    );
  }

  const link = `${pageContext.basePath}/${pageSlug}/employees`;
  const trackingLink = getTrackingUrl({
    to: link,
    trackingKey,
  });

  const showMore =
    totalEmployees > MINIMUM_EMPLOYEES_FOR_DETAIL_LINK
      ? {
          'data-testid': 'EMPLOYEES_SHOWMORE_LINK',
          to: link,
          onClick: (ev: MouseEvent) => {
            ev.preventDefault();
            window.open(trackingLink);
          },
          copyKey: 'EP_EMPLOYEES_LINK',
          number: totalEmployees,
        }
      : null;

  return (
    <div data-testid={'EMPLOYEES_MODULE'}>
      <ModuleHeader
        headlineCopyKey="EP_EMPLOYEES_HEADLINE"
        showMore={{
          ...showMore,
          isLoading:
            loadingEmployeesWithProfilePicture ||
            loadingEmployeesWithoutProfilePicture,
        }}
        externalEditInfo={
          !(
            loadingEmployeesWithProfilePicture ||
            loadingEmployeesWithoutProfilePicture
          ) && isEditing
            ? {
                copyKey: 'EP_EMPLOYEES_EDIT_HINT',
                cyTestId: 'EDIT_HINT_EMPLOYEES',
              }
            : null
        }
      />
      <EmployeesCarousel
        currentUserId={currentUserId}
        employeesList={employeesList}
        totalEmployees={totalEmployees}
        authenticated={authenticated}
        updateEmployeesCache={updateEmployeesCache}
      />
    </div>
  );
};

const EmployeesContainer = ({
  companyId,
  ...rest
}: EmployeesContainerProps) => (
  <EmployeesContext.Provider value={{ companyId }}>
    <Employees companyId={companyId} {...rest} />
  </EmployeesContext.Provider>
);

export { Employees };

export default withServerData(EmployeesContainer);
